import { type JanusCtx, type Row, ecLean, handBack, record } from '../ability/context';
import { combatCenter } from '../actions/ability-preconditions';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { readItemField } from '../compile/item-field-reader';
import { inventoryDefinition } from '../inventory/definition';
import { fallingShapeSupportFloor, resolveFloorMove, supportZAtOrBelow, type RestWorld, type RestShape } from '../inventory/floor-move';
import { creatureBaseSizeFt, heldMovementPosition, inventoryHasPosition, inventorySupport, type InventorySupportReaders } from '../movement/inventory-support';
import { SPEED } from '../movement/fall-check';
import { forcedMovementReaders } from '../ability/movement/check-push-and-pull-after-hit';
import type { ForcedMoveReaders } from '../movement/forced-movement';
import { encounterField } from '../../shared/array-types';
import { createVitalityReader } from '../compile/vitality-reader';
import { skipDeathDuty } from './skip';

export interface DeathLanding {
    changes: { preferred_speed_type: string; z_pos?: number };
    /** The live PC path resolves this through the shared fall-damage executor after landing.
     * An already-dead body just lands; it never takes another damage roll. */
    fallFt: number;
}

function groundingReaders(ctx: JanusCtx) {
    let readers: ForcedMoveReaders | undefined;
    return () => readers ??= forcedMovementReaders(ctx);
}

/** Scoped to one death composition: later deaths see the action's newly changed scene. */
export function createDeathGrounding(ctx: JanusCtx) {
    const read = groundingReaders(ctx);
    return {
        land: (target: Row) => deathLanding(ctx, target, read),
        drop: (item: Row, target: Row) => deathDropPosition(ctx, item, target, read),
    };
}

/** Resolve against the action's working scene, so earlier moves/destruction affect support. */
export function deathWorld(ctx: JanusCtx, at?: { x: number; y: number; z: number }, read = groundingReaders(ctx)): RestWorld {
    const raw = encounterField(ctx.encounter, 'encounter_shape');
    const depthAuthored = raw !== null && raw !== undefined && raw !== '';
    let shape: RestShape | null = null;
    if (depthAuthored) {
        try { shape = record(typeof raw === 'string' ? JSON.parse(raw) : raw) as RestShape | null; } catch { /* named below */ }
        if (!shape || !Array.isArray(shape.boxes)) handBack('death:landing-shape-not-held');
    }
    // Legacy support uses its native portrait sweep in planDeathLanding instead.
    if (!depthAuthored) return { shape, depthAuthored, rows: [] };
    if (at) {
        const floor = fallingShapeSupportFloor(shape!, at.x, at.y, at.z, 0.05);
        if (floor !== null && floor >= at.z - 0.05) return { shape, depthAuthored, rows: [] };
    }
    const encounterId = String(encounterField(ctx.encounter, 'encounter_id') ?? '').toUpperCase();
    return { shape, depthAuthored, rows: readInventoryRows(ctx.encounter, 'encounter_inventories_arr').flatMap(row => {
        if (String(readInventoryField(row, 'possess_id') ?? '').toUpperCase() !== encounterId
            || Number(readInventoryField(row, 'inventory_deleted') ?? 0) !== 0
            || Number(readInventoryField(row, 'item_qty') ?? 1) <= 0 || !inventoryHasPosition(row)) return [];
        const [, , z] = heldMovementPosition(row);
        if (at && z > at.z + 0.05) return [];
        const body = read().inventoryCollision?.(row);
        if (!body?.solid) return [];
        return [{ id: String(readInventoryField(row, 'inventory_id')), itemType: String(readItemField(inventoryDefinition(row, ctx.encounter.catalog, ctx.read.definition), 'item_type') ?? ''),
            z: z!, height: body.height, solid: body.solid, zone: body.obstacle }];
    }) };
}

export function deathLanding(ctx: JanusCtx, target: Row, read = groundingReaders(ctx)): DeathLanding {
    const at = combatCenter(target);
    // The native death/Dying branch does not query support at stored zero.
    const world = at?.z === 0 ? { shape: null, depthAuthored: false, rows: [] } : deathWorld(ctx, at ?? undefined, read);
    return planDeathLanding(target, ctx.encounter, world, { ...ctx.read,
        height: row => Number(ctx.read.value(row, 'creature_physics_height')) });
}

/** Death drops bypass voluntary movement restrictions, as the existing hand-drop lane does.
 * Preserve the object's active light/status rows and use the shared floor gravity executor. */
export function deathDropPosition(ctx: JanusCtx, item: Row, target: Row, read = groundingReaders(ctx)): { x: number; y: number; z: number } {
    const at = combatCenter(target);
    if (!at) handBack('death:drop-position-not-held');
    const world = deathWorld(ctx, at, read), id = String(readInventoryField(item, 'inventory_id'));
    const body = world.depthAuthored ? read().inventoryCollision?.(item) : null;
    const height = body?.height ?? 0;
    const result = resolveFloorMove({ encounterId: String(ecLean(target, 'encounter_id')), inventoryId: id,
        row: { possessId: String(ecLean(target, 'encounter_id')), deleted: false, qty: Number(readInventoryField(item, 'item_qty') ?? 1), ...at, transformSha: '' },
        to: at, elevated: true, explicitEditorSurface: true, actorId: null, immovable: false,
        itemType: String(readItemField(ctx.read.definition(String(readInventoryField(item, 'inventory_item_id'))), 'item_type') ?? ''), weight: null, shapeOverlayUnknown: false, hasVitality: null,
        gesture: 'drop', wholeRow: true }, world,
        { id, height, mounted: false, integrated: false, footprintAt: (x, y) => ({ kind: 'circle', x, y, r: 0, zMin: at.z, zMax: at.z + height }) });
    if (result.verdict !== 'commit') handBack(`death:drop:${result.reason}`);
    const fallFt = world.depthAuthored ? result.fallFt : deathLanding(ctx, target, read).fallFt;
    if (fallFt >= 10) {
        const templateId = String(readInventoryField(item, 'inventory_item_id'));
        const definition = ctx.read.definition(templateId);
        const vitality = createVitalityReader({ resolveInventoryInstance: () => null })({ ...item,
            catalog: { ...record(item.catalog), ...(definition ? { [templateId]: definition } : {}) } });
        if (Number(readItemField(vitality, 'v_max') ?? 0) > 0)
            skipDeathDuty(ctx, target, `${String(readItemField(definition, 'item_name') ?? 'Held object')} fell ${Math.round(fallFt * 100) / 100} ft; object fall damage is not yet supported.`);
    }
    return result.after;
}


/** apply_death_effect / apply_player_dying grounding. FT3D uses the existing support-at-or-below
 * planner, including authored floors, stairs, roof decks and solid props. Legacy XZ uses the
 * same substantial-overlap sweep as its native branch and stores the visual centre. */
export function planDeathLanding(target: Row, encounter: Row, world: RestWorld,
    read: InventorySupportReaders & { height(row: Row): number }): DeathLanding {
    const at = combatCenter(target);
    if (!at) handBack('death:landing-position-not-held');
    const changes: DeathLanding['changes'] = { preferred_speed_type: SPEED.walking };
    if (at.z === 0) return { changes, fallFt: 0 }; // native !empty(z_pos)
    if (world.depthAuthored) {
        const landing = supportZAtOrBelow(world, at.x, at.y, at.z, null);
        if (landing !== null && at.z > landing + 0.05) {
            changes.z_pos = landing;
            return { changes, fallFt: at.z - landing };
        }
        return { changes, fallFt: 0 };
    }
    const height = read.height(target);
    if (!Number.isFinite(height) || height <= 0) handBack('death:landing-height-not-held');
    const bottom = at.z - height / 2;
    if (bottom <= 2) return { changes, fallFt: 0 };
    const support = inventorySupport({ inventories: readInventoryRows(encounter, 'encounter_inventories_arr'),
        moverX: at.x, moverBase: creatureBaseSizeFt(target, read), bottom, grace: 2, authored: false, skipTransitions: false }, read);
    if ('unknown' in support) handBack(`death:landing-support:${support.unknown}:${support.row}`);
    const landing = support.top ?? 0;
    changes.z_pos = landing + height / 2;
    return { changes, fallFt: bottom - landing };
}
